import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { limitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { launchFixOf, monthLimitPosts, type LaunchSummaryRow } from '../releases/plan/rollout/launch/launchModel';
import { LimitUpsell } from '../../limitUpsell/LimitUpsell';
import { launchMessages } from '../releases/plan/rollout/launch/launchMessages';
import { launchPlatformName, PostsLaunchSummary } from '../releases/plan/rollout/launch/PostsLaunchSummary';
import { autoPostPlatforms, postingSplitOf } from '../releases/plan/rollout/launch/postingModes';
import type { CalendarPost, SerializedSocialChannel } from '../types';
import type { CalendarLaunch } from './useCalendarLaunch';

interface CalendarLaunchSummaryProps {
  launch: CalendarLaunch;
  /** The posts of the range on screen: they give each row its time. */
  posts: CalendarPost[];
  /** The channel rows: they say which posts go out by themselves (QA finding F02). */
  channels: SerializedSocialChannel[];
  /** True: the fix buttons are off (the demo artist). */
  isReadOnly: boolean;
  /** Opens the day panel on the post. */
  onOpenPost: (post: CalendarPost) => void;
  onOpenChannels: () => void;
}

/**
 * What "Schedule all drafts of this day" did (`PostsLaunchSummary`, the summary of the plan): "The
 * drafts of this day are scheduled", or "3 scheduled. 1 needs you:" with one row for each post
 * that stayed a draft. A post is named by its time and channel, in time order.
 */
const CalendarLaunchSummaryComponent: React.FC<CalendarLaunchSummaryProps> = ({
  launch,
  posts,
  channels,
  isReadOnly,
  onOpenPost,
  onOpenChannels,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { outcome, schedule } = launch;
  const postsByUuid = useMemo(() => new Map(posts.map((post) => [post.uuid, post])), [posts]);
  const split = useMemo(() => {
    if (outcome?.scheduledUuids === undefined) {
      return null;
    }
    const platformOf = new Map<string, string>(posts.map((post) => [post.uuid, post.platform]));
    return postingSplitOf(outcome.scheduledUuids, platformOf, autoPostPlatforms(channels));
  }, [channels, outcome, posts]);
  const rows = useMemo<LaunchSummaryRow[]>(() => {
    if (outcome === null) {
      return [];
    }
    return outcome.needsYou
      .map((one, index) => ({ one, index, post: postsByUuid.get(one.postUuid) }))
      .sort((a, b) => {
        // A post that left the range on screen goes last; the server order breaks a tie.
        const timeOf = (post: CalendarPost | undefined) =>
          post === undefined ? Number.MAX_SAFE_INTEGER : new Date(post.scheduledAt).getTime();
        const byTime = timeOf(a.post) - timeOf(b.post);
        return byTime !== 0 ? byTime : a.index - b.index;
      })
      .map(({ one, post }) => {
        const name = launchPlatformName(one.platform);
        return {
          postUuid: one.postUuid,
          platform: one.platform,
          reason: one.reason,
          detail: one.detail,
          fix: launchFixOf(one.reason),
          title:
            post === undefined
              ? intl.formatMessage(launchMessages.rowNameNoStep, { name })
              : intl.formatMessage(launchMessages.rowName, {
                  step: intl.formatDate(new Date(post.scheduledAt), {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                  name,
                }),
        };
      });
  }, [intl, outcome, postsByUuid]);

  const fix = useCallback(
    (row: LaunchSummaryRow) => {
      const post = postsByUuid.get(row.postUuid);
      switch (row.fix) {
        case 'answer':
          // "Answer" asks one time for every post that waits for an answer.
          schedule(rows.filter((one) => one.fix === 'answer').map((one) => one.postUuid));
          break;
        case 'reconnect':
          onOpenChannels();
          break;
        case 'plans':
          router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription });
          break;
        case 'openPost':
          if (post !== undefined) {
            onOpenPost(post);
          } else {
            router.push(SMLRoutes.promoteComposer, { postUuid: row.postUuid });
          }
      }
    },
    [onOpenChannels, onOpenPost, postsByUuid, router, rows, schedule]
  );

  // design: limit-upsell a11. The posts that did not fit the month: an add-on schedules them again.
  const refused = useMemo(() => monthLimitPosts(outcome), [outcome]);
  const refusedShortfall = useMemo(
    () => (isReadOnly ? null : limitShortfall('posts', refused.length, 0)),
    [isReadOnly, refused.length]
  );
  const scheduleRefused = useCallback(() => schedule(refused), [refused, schedule]);

  return (
    <>
      <PostsLaunchSummary
        outcome={outcome}
        rows={rows}
        isDisabled={isReadOnly || launch.isBusy}
        onFix={fix}
        onDismiss={launch.dismiss}
        allScheduledTitle={intl.formatMessage({
          id: 'app.sml.promote.calendar.scheduleAll.done',
          defaultMessage: 'The drafts of this day are scheduled',
        })}
        split={split}
        onConnectChannels={isReadOnly ? undefined : onOpenChannels}
      />
      <LimitUpsell shortfall={refusedShortfall} context="refused" onAdded={scheduleRefused} />
    </>
  );
};

export const CalendarLaunchSummary = TypedMemo(CalendarLaunchSummaryComponent);
